/**
 * Hoje (spec 018, US1): o que precisa de mim agora, e POR QUÊ.
 *
 * Medido em `5c943de`: o dashboard disparava uma requisição por mesa, nenhum
 * indicador era clicável e "precisa de resposta" só existia no chat. O Hoje
 * sai de UM endpoint (FR-001): os quatro indicadores e a fila vêm da mesma
 * classificação, sobre as mesmas linhas e no mesmo escopo — o número do
 * indicador bate com a lista que ele abre por construção, não por coincidência.
 *
 * Esta é a classificação: pura, sem IO, a mesma no servidor (que monta a
 * resposta) e nos testes. Os predicados SQL das visões da lista
 * (apps/api/src/shared/db/work-predicates.ts) são GÊMEOS destas regras, e o
 * teste de banco do módulo work confere os dois lados sobre os mesmos tickets.
 */
import { z } from 'zod';
import { ChatOriginSchema, ChatStatusSchema, type ChatStatus } from './chats';
import { Dateish } from './common';
import { DeskOriginSchema } from './desks';
import { slaTargetState } from './sla';
import {
  TicketSituationSchema,
  stageDeadline,
  ticketStuckInStage,
  type StageSituation,
} from './situations';
import {
  WAITING_CLIENT_SITUATIONS,
  WorkFocusSchema,
  WorkScopeSchema,
  type WorkFocus,
} from './work-views';

// ── Prazo: tipo e rótulo ────────────────────────────────────────────────────
/** Os dois alvos do SLA (v3): primeiro atendimento ("resposta") e solução. */
export const SLA_LEGS = ['attend', 'solve'] as const;
export const SlaLegSchema = z.enum(SLA_LEGS);
export type SlaLeg = z.infer<typeof SlaLegSchema>;

/** "Prazo de resposta" / "prazo de solução": a tela sempre diz QUAL prazo. */
export const SLA_LEG_LABELS: Record<SlaLeg, string> = { attend: 'resposta', solve: 'solução' };

// ── Motivos ─────────────────────────────────────────────────────────────────
/**
 * Motivos, do MAIS grave para o menos — a ordem explícita do plano da 018:
 * vencido > aguardando resposta > parado na etapa > (a vencer) > sem
 * responsável. "A vencer" não é vencido nem parado: entra depois dos dois.
 * `waiting_client` fecha a lista e NÃO entra na fila padrão — é trabalho que
 * espera o cliente, não a equipe; só aparece no indicador e no foco dele.
 */
export const WORK_REASON_KINDS = [
  'sla_overdue',
  'needs_reply',
  'stuck',
  'sla_due_soon',
  'unassigned',
  'waiting_client',
] as const;
export type WorkReasonKind = (typeof WORK_REASON_KINDS)[number];

/** Motivos que colocam um item na fila padrão (sem foco). */
export const QUEUE_REASON_KINDS: readonly WorkReasonKind[] = WORK_REASON_KINDS.filter(
  (k) => k !== 'waiting_client'
);

/** Que motivos cada indicador conta. "Prazo em risco" junta SLA e funil (FR-003). */
export const FOCUS_REASON_KINDS: Record<WorkFocus, readonly WorkReasonKind[]> = {
  needs_reply: ['needs_reply'],
  at_risk: ['sla_overdue', 'stuck', 'sla_due_soon'],
  unassigned: ['unassigned'],
  waiting_client: ['waiting_client'],
};

export const WorkReasonSchema = z.discriminatedUnion('kind', [
  /** SLA vencido: qual prazo e quando venceu */
  z.object({ kind: z.literal('sla_overdue'), leg: SlaLegSchema, dueAt: Dateish }),
  /** o cliente espera resposta desde a mensagem pública mais antiga sem resposta */
  z.object({ kind: z.literal('needs_reply'), since: Dateish }),
  /** parado na etapa: desde quando está nela e quando o prazo dela venceu (017, FR-008) */
  z.object({ kind: z.literal('stuck'), stageName: z.string(), since: Dateish, dueAt: Dateish }),
  /** SLA a vencer: menos de 25% da janela restando */
  z.object({ kind: z.literal('sla_due_soon'), leg: SlaLegSchema, dueAt: Dateish }),
  /** sem responsável desde que nasceu */
  z.object({ kind: z.literal('unassigned'), since: Dateish }),
  /** numa etapa que espera o cliente ou o contato, desde que entrou nela */
  z.object({
    kind: z.literal('waiting_client'),
    situation: z.enum(WAITING_CLIENT_SITUATIONS),
    since: Dateish,
  }),
]);
export type WorkReason = z.infer<typeof WorkReasonSchema>;

const ms = (d: Date | string): number => new Date(d).getTime();

const RANK: Record<WorkReasonKind, number> = Object.fromEntries(
  WORK_REASON_KINDS.map((kind, i) => [kind, i])
) as Record<WorkReasonKind, number>;

/**
 * Instante que desempata DENTRO do mesmo motivo — "desempate por
 * antiguidade": o prazo que venceu primeiro, a espera mais antiga.
 */
export function reasonAnchor(reason: WorkReason): number {
  switch (reason.kind) {
    case 'sla_overdue':
    case 'sla_due_soon':
    case 'stuck':
      return ms(reason.dueAt);
    case 'needs_reply':
    case 'unassigned':
    case 'waiting_client':
      return ms(reason.since);
  }
}

/** Mais grave primeiro; no mesmo motivo, o mais antigo primeiro. */
export function compareReasons(a: WorkReason, b: WorkReason): number {
  return RANK[a.kind] - RANK[b.kind] || reasonAnchor(a) - reasonAnchor(b);
}

// ── Classificação de um ticket ──────────────────────────────────────────────
/** O que a classificação precisa de um ticket — as colunas cruas, nada derivado. */
export interface TicketWorkInput {
  responsibleId: number | null;
  createdAt: Date | string;
  awaitingReplySince: Date | string | null;
  stageName: string;
  stageSituation: StageSituation;
  stageEnteredAt: Date | string;
  stageMaxTimeMinutes: number | null;
  attendDueAt: Date | string | null;
  solveDueAt: Date | string | null;
  attendedAt: Date | string | null;
  slaPausedAt: Date | string | null;
  closedAt: Date | string | null;
  canceledAt: Date | string | null;
}

/**
 * Todos os motivos de um ticket, do mais grave para o menos. Fechado ou
 * cancelado não tem motivo nenhum.
 *
 * - SLA: pela MESMA regra do chip da lista (slaTargetState) — pausado não vence
 *   nem "está a vencer"; de cada estado fica o alvo mais urgente;
 * - resposta: `awaitingReplySince` (mantido nos pontos de escrita);
 * - funil: parado na etapa (ticketStuckInStage da 017);
 * - sem responsável, e etapa que espera o cliente/contato.
 */
export function ticketWorkReasons(t: TicketWorkInput, now: Date): WorkReason[] {
  if (t.closedAt != null || t.canceledAt != null) return [];
  const reasons: WorkReason[] = [];

  if (t.slaPausedAt == null) {
    const legs: Array<{ leg: SlaLeg; dueAt: Date | string | null; doneAt: Date | string | null }> = [
      { leg: 'attend', dueAt: t.attendDueAt, doneAt: t.attendedAt },
      { leg: 'solve', dueAt: t.solveDueAt, doneAt: null },
    ];
    let overdue: { leg: SlaLeg; dueAt: Date } | null = null;
    let soon: { leg: SlaLeg; dueAt: Date } | null = null;
    for (const l of legs) {
      if (l.dueAt == null) continue;
      const state = slaTargetState({
        dueAt: l.dueAt,
        doneAt: l.doneAt,
        pausedAt: null,
        startAt: t.createdAt,
        now,
      });
      const dueAt = new Date(l.dueAt);
      if (state === 'overdue' && (overdue === null || dueAt < overdue.dueAt)) overdue = { leg: l.leg, dueAt };
      if (state === 'due_soon' && (soon === null || dueAt < soon.dueAt)) soon = { leg: l.leg, dueAt };
    }
    if (overdue) reasons.push({ kind: 'sla_overdue', ...overdue });
    if (soon) reasons.push({ kind: 'sla_due_soon', ...soon });
  }

  if (t.awaitingReplySince != null) {
    reasons.push({ kind: 'needs_reply', since: new Date(t.awaitingReplySince) });
  }

  const situation = t.stageSituation;
  if (ticketStuckInStage({ situation, stageEnteredAt: t.stageEnteredAt }, t.stageMaxTimeMinutes, now)) {
    reasons.push({
      kind: 'stuck',
      stageName: t.stageName,
      since: new Date(t.stageEnteredAt),
      // não-nulo aqui: parado implica etapa com prazo
      dueAt: stageDeadline(t.stageEnteredAt, t.stageMaxTimeMinutes) ?? new Date(t.stageEnteredAt),
    });
  }

  if (t.responsibleId === null) reasons.push({ kind: 'unassigned', since: new Date(t.createdAt) });

  if (situation === 'waiting_client' || situation === 'waiting_contact') {
    reasons.push({ kind: 'waiting_client', situation, since: new Date(t.stageEnteredAt) });
  }

  return reasons.sort(compareReasons);
}

/**
 * Conversa sem atendimento (ou cujo atendimento não representa mais o
 * trabalho): só "precisa de resposta" — desde a mensagem do cliente mais
 * antiga depois da última resposta de um atendente.
 */
export function chatWorkReasons(c: { awaitingSince: Date | string | null }): WorkReason[] {
  return c.awaitingSince == null ? [] : [{ kind: 'needs_reply', since: new Date(c.awaitingSince) }];
}

// ── Ação ────────────────────────────────────────────────────────────────────
export const WORK_ACTIONS = ['reply', 'assume', 'open'] as const;
export const WorkActionSchema = z.enum(WORK_ACTIONS);
export type WorkAction = z.infer<typeof WorkActionSchema>;

export const WORK_ACTION_LABELS: Record<WorkAction, string> = {
  reply: 'Responder',
  assume: 'Assumir',
  open: 'Abrir',
};

/**
 * Ação de um ticket na fila:
 * - o cliente espera: Responder se é meu; Assumir se está sem dono; Abrir se
 *   é de outra pessoa;
 * - sem dono (e ninguém esperando): Assumir;
 * - o resto: Abrir.
 * Ticket espelhado do TiFlux (011) não troca de responsável no ERP — Assumir
 * seria recusado com 409; mas o WhatsApp dele é do ERP e continua
 * respondível, então quem espera resposta nele vira Responder.
 */
export function ticketWorkAction(
  t: { responsibleId: number | null; mirrored: boolean },
  reasons: readonly WorkReason[],
  userId: number
): WorkAction {
  const unassigned = t.responsibleId === null;
  if (reasons.some((r) => r.kind === 'needs_reply')) {
    if (t.responsibleId === userId) return 'reply';
    if (unassigned) return t.mirrored ? 'reply' : 'assume';
    return 'open';
  }
  if (unassigned && !t.mirrored) return 'assume';
  return 'open';
}

/** Conversa: da fila, Assumir; minha em atendimento, Responder; de outra pessoa, Abrir. */
export function chatWorkAction(
  c: { status: ChatStatus; responsibleId: number | null },
  userId: number
): WorkAction {
  if (c.status === 'inbox') return 'assume';
  if (c.status === 'in_attendance' && c.responsibleId === userId) return 'reply';
  return 'open';
}

// ── Contrato do GET /work/today ─────────────────────────────────────────────
export const WorkTodayQuerySchema = z.object({
  scope: WorkScopeSchema.default('mine'),
  /** indicador clicado: a lista passa a ser a dos itens com esse motivo */
  focus: WorkFocusSchema.optional(),
});
export type WorkTodayQuery = z.infer<typeof WorkTodayQuerySchema>;

const Ref = z.object({ id: z.number().int(), name: z.string() });

export const WorkTicketItemSchema = z.object({
  kind: z.literal('ticket'),
  ticketNumber: z.number().int(),
  title: z.string(),
  client: Ref,
  desk: Ref.extend({ origin: DeskOriginSchema }),
  /** espelhado do TiFlux (spec 024) — o predicado único, nunca a mesa */
  mirrored: z.boolean(),
  stage: Ref,
  situation: TicketSituationSchema,
  responsible: Ref.nullable(),
  resourceLabel: z.string().nullable(),
  /** o motivo que a tela mostra: o mais grave (da fila ou do foco pedido) */
  reason: WorkReasonSchema,
  /** todos os motivos, do mais grave para o menos */
  reasons: z.array(WorkReasonSchema).min(1),
  action: WorkActionSchema,
});
export type WorkTicketItem = z.infer<typeof WorkTicketItemSchema>;

export const WorkChatItemSchema = z.object({
  kind: z.literal('chat'),
  chatId: z.number().int(),
  origin: ChatOriginSchema,
  status: ChatStatusSchema,
  requestorName: z.string(),
  client: Ref.nullable(),
  desk: Ref.nullable(),
  responsible: Ref.nullable(),
  /**
   * Ticket vinculado que NÃO representa mais o trabalho — encerrado, ou fora
   * da minha visão. null = conversa sem atendimento.
   */
  ticketNumber: z.number().int().nullable(),
  reason: WorkReasonSchema,
  reasons: z.array(WorkReasonSchema).min(1),
  action: WorkActionSchema,
});
export type WorkChatItem = z.infer<typeof WorkChatItemSchema>;

export const WorkItemSchema = z.discriminatedUnion('kind', [WorkTicketItemSchema, WorkChatItemSchema]);
export type WorkItem = z.infer<typeof WorkItemSchema>;

export const WorkCountersSchema = z.object({
  needsReply: z.number().int(),
  atRisk: z.number().int(),
  unassigned: z.number().int(),
  waitingClient: z.number().int(),
});
export type WorkCounters = z.infer<typeof WorkCountersSchema>;

/** Teto de itens na resposta; os contadores contam TUDO, e `total` diz quanto ficou de fora. */
export const WORK_TODAY_LIMIT = 200;

export const WorkTodaySchema = z.object({
  scope: WorkScopeSchema,
  focus: WorkFocusSchema.nullable(),
  counters: WorkCountersSchema,
  /** o "Precisa de resposta" separado: atendimentos x conversas sem atendimento */
  needsReplyBreakdown: z.object({ tickets: z.number().int(), chats: z.number().int() }),
  /** "Aguardando cliente" separado pela situação (cliente x contato) */
  waitingClientBreakdown: z.object({
    waiting_client: z.number().int(),
    waiting_contact: z.number().int(),
  }),
  items: z.array(WorkItemSchema),
  /** itens da fila (ou do foco) antes do teto — igual ao indicador quando há foco */
  total: z.number().int(),
  truncated: z.boolean(),
  generatedAt: Dateish,
});
export type WorkToday = z.infer<typeof WorkTodaySchema>;

/** Item já classificado, antes de escolher o motivo exibido. */
export type ClassifiedWorkItem =
  | Omit<WorkTicketItem, 'reason'>
  | Omit<WorkChatItem, 'reason'>;

const hasAny = (item: { reasons: readonly WorkReason[] }, kinds: readonly WorkReasonKind[]) =>
  item.reasons.some((r) => kinds.includes(r.kind));

/** O motivo exibido: o mais grave entre os do foco (ou da fila, sem foco). */
function displayReason(
  item: { reasons: readonly WorkReason[] },
  kinds: readonly WorkReasonKind[]
): WorkReason | null {
  // reasons já vem ordenada do mais grave para o menos
  return item.reasons.find((r) => kinds.includes(r.kind)) ?? null;
}

const itemKey = (item: ClassifiedWorkItem): number =>
  item.kind === 'ticket' ? item.ticketNumber : -item.chatId;

/**
 * Monta a resposta do Hoje a partir de TODOS os itens classificados do
 * escopo. Contadores, foco e fila saem da MESMA lista — é a garantia de que o
 * indicador e a lista que ele abre dizem o mesmo número. Um item aparece UMA
 * vez, com o motivo mais grave; sem foco, item que só espera o cliente fica
 * de fora da fila (o indicador dele continua contando).
 */
export function buildWorkToday(input: {
  scope: WorkToday['scope'];
  focus?: WorkFocus | null;
  items: readonly ClassifiedWorkItem[];
  now: Date;
  limit?: number;
}): WorkToday {
  const limit = input.limit ?? WORK_TODAY_LIMIT;
  const focus = input.focus ?? null;
  const count = (f: WorkFocus) => input.items.filter((i) => hasAny(i, FOCUS_REASON_KINDS[f])).length;

  const kinds = focus ? FOCUS_REASON_KINDS[focus] : QUEUE_REASON_KINDS;
  const pool: WorkItem[] = [];
  for (const item of input.items) {
    const reason = displayReason(item, kinds);
    if (reason) pool.push({ ...item, reason } as WorkItem);
  }
  pool.sort((a, b) => compareReasons(a.reason, b.reason) || itemKey(a) - itemKey(b));

  const needsReply = input.items.filter((i) => hasAny(i, ['needs_reply']));
  const waiting = input.items.flatMap((i) =>
    i.reasons.filter((r): r is Extract<WorkReason, { kind: 'waiting_client' }> => r.kind === 'waiting_client')
  );
  return {
    scope: input.scope,
    focus,
    counters: {
      needsReply: count('needs_reply'),
      atRisk: count('at_risk'),
      unassigned: count('unassigned'),
      waitingClient: count('waiting_client'),
    },
    needsReplyBreakdown: {
      tickets: needsReply.filter((i) => i.kind === 'ticket').length,
      chats: needsReply.filter((i) => i.kind === 'chat').length,
    },
    waitingClientBreakdown: {
      waiting_client: waiting.filter((r) => r.situation === 'waiting_client').length,
      waiting_contact: waiting.filter((r) => r.situation === 'waiting_contact').length,
    },
    items: pool.slice(0, limit),
    total: pool.length,
    truncated: pool.length > limit,
    generatedAt: input.now,
  };
}

// ── Estado de resposta de um ticket (awaiting_reply_since) ──────────────────
/**
 * Quem falou por último em PÚBLICO decide se o cliente espera (FR-002):
 * - cliente/contato (portal, e-mail, WhatsApp de conversa vinculada): passa a
 *   esperar — desde a mensagem MAIS ANTIGA sem resposta, então uma segunda
 *   mensagem não "rejuvenesce" a espera;
 * - equipe, em público (comentário público, WhatsApp): respondeu, limpa;
 * - nota interna não é resposta; automação, espelho do TiFlux e a nota do bot
 *   (spec 022) não respondem nem perguntam — nada muda.
 * GÊMEA do SQL em apps/api/src/shared/db/ticket-reply-state.ts.
 */
export type ReplyAuthor = 'client' | 'staff' | 'system' | 'bot';

export function nextAwaitingReplySince(
  current: Date | null,
  message: { author: ReplyAuthor; internal: boolean; at: Date }
): Date | null {
  if (message.internal) return current;
  if (message.author === 'client') return current ?? message.at;
  if (message.author === 'staff') return null;
  return current;
}

/**
 * Ticket que JÁ nasce com o cliente esperando: aberto pelo próprio cliente
 * (portal, e-mail, formulário público) — a solicitação inicial é a mensagem
 * dele, e ninguém respondeu ainda. Aberto pela equipe (web, api, agendado) ou
 * espelhado do TiFlux, não.
 */
export function awaitsReplyOnCreate(createdVia: string): boolean {
  return createdVia === 'portal' || createdVia === 'email' || createdVia === 'form';
}

